import type { IMenuProps, IOmit } from '@sixui/ui';
import { useCallback } from 'react';
import { faPen, faTrashCan } from '@fortawesome/pro-light-svg-icons';
import {
  faEllipsisVertical,
  faPause,
  faPlay,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, Menu, MenuItem } from '@sixui/ui';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IAd } from '~/lib/api/modules/Ads';
import { useNavigate } from '~/components/utils/Navigation';
import { useDeleteAdMutation } from '~/lib/api/modules/Ads/hooks/useDeleteAdMutation';
import { useUpdateAdMutation } from '~/lib/api/modules/Ads/hooks/useUpdateAdMutation';

export type IAdListItemMenuProps = IContainerProps &
  IOmit<IMenuProps, 'trigger' | 'children'> & {
    ad: IAd;
  };

export const AdListItemMenu: React.FC<IAdListItemMenuProps> = (props) => {
  const { sx, ad, ...other } = props;
  const { t } = useTranslation();
  const deleteAdMutation = useDeleteAdMutation();
  const updateAdMutation = useUpdateAdMutation();
  const navigate = useNavigate();

  const startAt = ad.limits?.notBefore && DateTime.fromISO(ad.limits.notBefore);
  const endAt = ad.limits?.notAfter && DateTime.fromISO(ad.limits.notAfter);
  const started = startAt ? startAt < DateTime.now() : true;
  const ended = endAt ? endAt < DateTime.now() : false;
  const running = started && !ended;
  const canSuspend = running && ad.active;
  const canResume = running && !ad.active;

  const processing = deleteAdMutation.isLoading || updateAdMutation.isLoading;

  const handleDelete = useCallback((): void => {
    if (deleteAdMutation.isLoading) {
      return;
    }

    // eslint-disable-next-line no-alert
    const doDeletion = confirm(t('AdListItemMenu.dialog.confirmDeletion.text'));

    if (doDeletion) {
      void deleteAdMutation
        .mutateAsync({
          appId: ad.appId,
          adId: ad._id,
        })
        .then(() => navigate(`/apps/${ad.appId}/ads`))
        .catch(() => {
          // TODO: handle error (snackbar)
        });
    }
  }, [deleteAdMutation, ad.appId, ad._id, navigate, t]);

  const handlePause = useCallback(
    (paused: boolean) => {
      if (updateAdMutation.isLoading) {
        return;
      }

      void updateAdMutation
        .mutateAsync({
          appId: ad.appId,
          adId: ad._id,
          body: {
            active: !paused,
          },
        })
        .catch(() => {
          // TODO: handle error (snackbar)
        });
    },
    [updateAdMutation, ad.appId, ad._id],
  );

  return (
    <Menu
      trigger={({ getProps }) => (
        <IconButton
          icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
          loading={processing}
          {...getProps()}
        />
      )}
      placement="bottom-end"
      sx={sx}
      {...other}
    >
      {canSuspend && (
        <MenuItem
          label={t('common.suspend')}
          leadingIcon={<FontAwesomeIcon icon={faPause} />}
          onClick={() => handlePause(true)}
        />
      )}
      {canResume && (
        <MenuItem
          label={t('common.resume')}
          leadingIcon={<FontAwesomeIcon icon={faPlay} />}
          onClick={() => handlePause(false)}
        />
      )}
      <MenuItem
        label={t('common.edit')}
        leadingIcon={<FontAwesomeIcon icon={faPen} />}
        href={`/apps/${ad.appId}/ads/${ad._id}/update`}
      />
      <MenuItem
        label={t('common.delete')}
        leadingIcon={<FontAwesomeIcon icon={faTrashCan} />}
        onClick={handleDelete}
        variant="danger"
      />
    </Menu>
  );
};
